import * as React from 'react';
import {
  GetChannelLookupAPI,
  GetChannelByIdAPI,
  SaveChannelArchitectureAPI,
  SaveGradeArchitectureAPI,
  SaveMinutageArchitectureAPI,
} from 'api';
import CAGrading from './CAGrading';
import CAMinutage from './CAMinutage';
import CAProgrammmes from './CAProgrammes';
import { NumericTextbox } from 'op2mise-react-widgets';
import Header from 'shared/components/header/Header';
import Select from 'shared/components/select/Select';
import { addDaysToDate, getMondayOfTheWeek } from 'utils';
import useStore from 'store/AccountStore';
import './ChannelArchitecture.css';
import { useCallbackPrompt } from 'components/_reusable/blocker/UsePromptComponent.ts';
import ConfirmLeaveModal from 'shared/components/confirm-leave-modal/ConfirmLeaveModal';
import { schedulesReducer } from './reducers/schedules.reducer';
import useNotification from 'store/NotificationStore';
import { calculateDates, getChannelSession } from 'utils';
import moment from 'moment';
import { getBlockName } from './utils/architecture.utils';
import { Internationalization } from '@syncfusion/ej2-base';
import { Constants } from './helper/constants';
import OpSelect from 'op2mise-react-widgets/OpSelect/OpSelect';
import { getMondayZeroToSixSchedules } from './helper/getMondayZeroToSixSchedules';

const ChannelArchitecture = () => {
  const { user, updateUser, updateChannel } = useStore((state) => state);
  const { weekdays } = Constants;
  const [activatedTab, setActivatedTab] = React.useState('Program');
  const [channelsList, setChannelsList] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [numberOfWeeks, setNumberOfWeeks] = React.useState(1);
  const [quickInfoDetails, setQuickInfoDetails] = React.useState({
    header: null,
    content: null,
  });
  const [selectedChannel, setSelectedChannel] = React.useState(0);
  const [channelInfo, setChannelInfo] = React.useState({
    channelId: 0,
    channelName: null,
    timezone: null,
    timezoneID: null,
  });
  const [startDate, setStartDate] = React.useState(
    getMondayOfTheWeek(new Date())
  );
  const [endDate, setEndDate] = React.useState(addDaysToDate(startDate, 7));
  const [isDirty, setIsDirty] = React.useState(false);
  const { success, danger } = useNotification((state) => state);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    isDirty
  );
  const [schedules, dispatch] = React.useReducer(schedulesReducer, {});
  const [showDirtyForm, setShowDirtyForm] = React.useState(false);
  const [next, setNext] = React.useState();
  const [grading, setGrading] = React.useState({});
  const [minutage, setMinutage] = React.useState({});
  const [allEvents, setAllEvents] = React.useState({});

  const tabOptions = ['Program', 'Grading', 'Minutage'];

  const getSession = (channels) => {
    const { channelId, tab } = user;
    var sessionChannel = getChannelSession(channelId, channels);
    handleSelectChannel(sessionChannel);

    if (tab && tabOptions.includes(tab)) {
      setActivatedTab(tab);
    } else {
      updateUser({ tab: tabOptions[0] });
    }
  };

  const getChannelsAPI = () => {
    GetChannelLookupAPI({
      onSuccess: (res) => {
        if (res.length > 0) {
          setChannelsList(res);
          getSession(res);
        }
      },
      // setLoader: setIsLoading,
    });
  };

  const handleSchedulerOverlapCheck = (events) => {
    if (!Array.isArray(events) || events.length <= 1) return false;

    // Normalize and sort by StartTime
    const normalized = events
      .map(e => ({
        id: e.Id,
        start: new Date(e.StartTime).getTime(),
        end: new Date(e.EndTime).getTime(),
      }))
      .sort((a, b) => a.start - b.start);

    // Compare each event to the next
    return normalized.some((event, i, arr) => {
      const next = arr[i + 1];
      return next && event.end > next.start;
    });
  };

  const handleSaveChannelArchitecture = React.useCallback(
    (events) => {
      const hasOverlaps = handleSchedulerOverlapCheck(events);
      if (hasOverlaps) {
        danger('Unable to save channel architecture. One or more blocks overlap with existing durations. Please review the affected weeks, days, start, or end times to resolve scheduling conflicts before saving.')
        if (showPrompt) cancelNavigation();
        setShowDirtyForm(false);
        return;
      }

      let blocks = [];
      if (events.length) {
        blocks = events
          .map((schedule) => {
            return {
              availableDuration: schedule?.AvailableDuration
                ? schedule.AvailableDuration
                : 0,
              blockName: getBlockName(schedule.DayOfWeek, schedule.StartTime),
              blockReference: schedule?.BlockReference,
              channelID: channelInfo.channelId,
              dayOfWeek: schedule.DayOfWeek,
              endTime: moment(schedule.EndTime, 'HH:mm:ss').format('HH:mm:ss'),
              genre: schedule?.Genre ? schedule.Genre : '',
              layout: schedule?.Layout,
              link: schedule?.IsLinked ? schedule.IsLinked : false,
              maxCount: schedule?.MaxCount ? schedule.MaxCount : 0,
              seasonID: schedule?.SeasonID
                ? schedule.SeasonID.toString()
                : null,
              seriesID: schedule?.SeriesID ? +schedule.SeriesID : null,
              seriesName: schedule?.SeriesName ? schedule.SeriesName : null,
              sequential: false,
              startTime: moment(schedule.StartTime, 'HH:mm:ss').format(
                'HH:mm:ss'
              ),
              titleGroupID: schedule?.TitleGroupID
                ? +schedule.TitleGroupID
                : null,
              titleGroupName: schedule?.TitleGroupName
                ? schedule.TitleGroupName
                : null,
              // type: schedule?.Type ? schedule?.Type : "Movies",
              type: schedule?.Type
                ? schedule.Type === 'Title Group'
                  ? 'TitleGroup'
                  : schedule.Type
                : 'Movie',
              week: schedule?.Week,
              id: isNaN(schedule?.Id) ? 0 : schedule.Id,
            };
          })
          .filter((block) => block.week === 1);
      }
      SaveChannelArchitectureAPI({
        queryParams: {
          channelId: channelInfo.channelId,
          payload: { blocks: blocks },
        },
        onSuccess: (res) => {
          if (res) {
            setIsDirty(false);
            if (showDirtyForm) {
              handleDiscardDirtyForm();
            }
            if (showPrompt) confirmNavigation();
            if (!showPrompt && !showDirtyForm) success('Successfully saved!');
          }
        },
      });
    },
    [channelInfo, showPrompt, showDirtyForm]
  );

  const handleSaveGradeArchitecture = React.useCallback(
    (grading) => {
      let blocks = [];

      if (grading.length) {
        blocks = grading.map((schedule) => {
          return {
            id: isNaN(schedule?.Id) ? 0 : schedule.Id,
            channelID: channelInfo.channelId,
            dayOfWeek: schedule.DayOfWeek,
            startTime: moment(schedule.StartTime, 'HH:mm:ss').format(
              'HH:mm:ss'
            ),
            endTime: moment(schedule.EndTime, 'HH:mm:ss').format('HH:mm:ss'),
            grade: schedule?.Grade ? schedule.Grade : 0,
            value: 0,
            miutage: 0,
          };
        });
      }
      SaveGradeArchitectureAPI({
        queryParams: {
          channelId: channelInfo.channelId,
          payload: { gradeMinutageArchitectures: blocks },
        },
        onSuccess: (res) => {
          setIsDirty(false);
          if (showDirtyForm) {
            handleDiscardDirtyForm();
          }
          if (showPrompt) confirmNavigation();
          if (!showPrompt && !showDirtyForm) success('Successfully saved!');
        },
      });
    },
    [channelInfo]
  );

  const handleSaveMinutageArchitecture = React.useCallback(
    (minutage) => {
      let blocks = [];

      if (minutage.length) {
        const instance = new Internationalization();

        blocks = minutage.map((schedule) => {
          const day = instance.formatDate(schedule.StartTime, {
            skeleton: 'E',
          });
          return {
            id: isNaN(schedule?.Id) ? 0 : schedule.Id,
            channelID: channelInfo.channelId,
            dayOfWeek: weekdays[day],
            startTime: moment(schedule.StartTime, 'HH:mm:ss').format(
              'HH:mm:ss'
            ),
            endTime: moment(schedule.EndTime, 'HH:mm:ss').format('HH:mm:ss'),
            grade: 0,
            value: 0,
            minutage: schedule?.Minutage ? schedule.Minutage : 0,
          };
        });
      }
      SaveMinutageArchitectureAPI({
        queryParams: {
          channelId: channelInfo.channelId,
          payload: { minutageArchitectures: blocks },
        },
        onSuccess: (res) => {
          setIsDirty(false);
          if (showDirtyForm) {
            handleDiscardDirtyForm();
          }
          if (showPrompt) confirmNavigation();
          if (!showPrompt && !showDirtyForm) success('Successfully saved!');
        },
      });
    },
    [channelInfo]
  );

  const handleSaveDirty = React.useCallback(() => {
    switch (activatedTab) {
      case 'Program':
        handleSaveChannelArchitecture(allEvents);
        break;
      case 'Grading':
        handleSaveGradeArchitecture(grading);
        break;
      case 'Minutage':
        handleSaveMinutageArchitecture(minutage);
        break;
    }
  }, [
    activatedTab,
    allEvents,
    grading,
    minutage,
    showDirtyForm,
    showPrompt,
    schedules.programmes,
  ]);

  const handleDiscardDirtyForm = () => {
    setIsDirty(false);
    if (next.type === 'tab') handleTabChange(next.value, false);
    if (next.type == 'channel') {
      const args = next.value;
      setSelectedChannel(args.channelId);
      setChannelInfo(args);
    }
    setShowDirtyForm(false);
  };

  const handleTabChange = (tab, dirty) => {
    if (dirty) {
      setNext({ type: 'tab', value: tab });
      setShowDirtyForm(true);
    } else {
      setActivatedTab(tab);
      updateUser({ tab: tab });
    }
  };

  const handleGetEventDate = React.useCallback(
    (args, attribute) => {
      const tempDate = calculateDates(
        args.dayOfWeek,
        args.week ?? 1,
        args.startTime,
        args.endTime,
        { startDate: startDate, endDate: endDate }
      )[attribute];
      return tempDate;
    },
    [startDate, endDate]
  );

  const handleConstructGridData = React.useCallback(
    (data, removeDuplicates) => {
      return getMondayZeroToSixSchedules(
        [...data],
        { startDate, endDate },
        user?.clockType === 'Broadcast',
        removeDuplicates
      );
    },
    [user, startDate, endDate]
  );

  const headerComponent = React.useMemo(() => {
    const activatedTabStyle = {
      backgroundColor: 'transparent',
      color: '#FFFFFF',
      border: 'none',
      fontWeight: 'bold',
    };
    const defaultTabStyle = {
      backgroundColor: 'transparent',
      color: '#FAFAFA80',
      border: 'none',
    };
    const headerTabs = ['Program', 'Grading', 'Minutage'];
    return (
      <div>
        {headerTabs.map((tab, index) => (
          <button
            key={index}
            type="button"
            className="btn"
            style={tab === activatedTab ? activatedTabStyle : defaultTabStyle}
            onClick={() => {
              handleTabChange(tab, isDirty);
            }}
          >
            {' '}
            {tab}
          </button>
        ))}
      </div>
    );
  }, [activatedTab, isDirty]);

  const schedulerProps = React.useMemo(() => {
    return {
      heightBuffer: 177,
      // schedule: { start: startDate, end: endDate },
      showHeaderBar: false,
      quickInfoDetails,
      headerComponent,
    };
  }, [headerComponent]);

  const programmeProps = React.useMemo(() => {
    return {
      schedulerProps,
      numberOfWeeks,
      setNumberOfWeeks,
      startDate,
      endDate,
      handleGetEventDate,
      isDirty,
      setIsDirty,
      schedules,
      dispatch,
      handleSaveChannelArchitecture,
      setAllEvents,
      channelInfo,
      handleConstructGridData,
    };
  }, [
    schedulerProps,
    startDate,
    endDate,
    selectedChannel,
    numberOfWeeks,
    isDirty,
    schedules,
    channelInfo,
    handleGetEventDate,
    handleConstructGridData,
  ]);

  const gradingProps = React.useMemo(() => {
    return {
      schedulerProps,
      channelInfo,
      startDate,
      endDate,
      handleGetEventDate,
      isDirty,
      setIsDirty,
      setGrading,
      handleSaveGradeArchitecture,
      handleConstructGridData,
    };
  }, [
    schedulerProps,
    isDirty,
    channelInfo,
    handleGetEventDate,
    handleConstructGridData,
  ]);

  const minutageProps = React.useMemo(() => {
    return {
      schedulerProps,
      channelInfo,
      startDate,
      endDate,
      handleGetEventDate,
      isDirty,
      setIsDirty,
      setMinutage,
      handleSaveMinutageArchitecture,
      handleConstructGridData,
    };
  }, [
    schedulerProps,
    isDirty,
    channelInfo,
    handleGetEventDate,
    handleConstructGridData,
  ]);

  const activeCAComponent = React.useMemo(
    () =>
      activatedTab === 'Grading' ? (
        <CAGrading {...gradingProps} />
      ) : activatedTab === 'Minutage' ? (
        <CAMinutage {...minutageProps} />
      ) : (
        <CAProgrammmes {...programmeProps} />
      ),
    [activatedTab, endDate, channelInfo, numberOfWeeks, schedules, isDirty]
  );

  const renderNumericTextbox = React.useCallback(() => {
    return (
      <NumericTextbox
        format="##"
        value={activatedTab === 'Program' ? numberOfWeeks : 1}
        min={1}
        max={52}
        showSpinButton={false}
        onBlur={(args) => {
          setNumberOfWeeks(parseInt(args.target.value));
        }}
        enabled={channelsList.length > 0 ? activatedTab === 'Program' : false}
        cssClass="e-op2mise-numeric-textbox"
        label="Number of Weeks:"
      />
    );
  }, [activatedTab, numberOfWeeks, channelsList]);

  const handleSelectChannel = (args) => {
    if (isDirty) {
      setNext({ type: 'channel', value: args });
      setShowDirtyForm(true);
    } else {
      setSelectedChannel(args.channelId);
      setChannelInfo(args);
      // Save to session
      updateChannel(args);
    }
  };

  React.useEffect(() => {
    setIsLoading(true);
    getChannelsAPI();
  }, []);

  React.useEffect(() => {
    if (selectedChannel) {
      GetChannelByIdAPI({
        queryParams: selectedChannel,
        onSuccess: (res) => {
          setChannelInfo({
            channelName: res.channelName,
            channelId: res.channelId,
            timezone: res.timeZoneName,
            timeoneID: res.timeZoneID,
          });
          // Save to session
          updateChannel(res);
        },
      });
    }
  }, [selectedChannel]);

  return (
    <div>
      <Header
        title={'Architecture'}
        childComponent={
          <div className="d-flex align-items-center gap-4">
            {/* Channels Selection/Dropdown */}
            <div className="architecture-banner-channel-selector">
              <span className="architecture-banner-channel-label">
                Channel:
              </span>
              <div>
                {/** Re-locate this re-usable component to `op2mise-react-widgets */}
                <OpSelect
                  allowFiltering={true}
                  dataSource={
                    channelsList
                      ? channelsList.map((item) => ({
                          id: item.channelId,
                          name: item.channelName,
                          ...item,
                        }))
                      : null
                  }
                  fields={{ text: 'name', value: 'id' }}
                  filterBarPlaceholder="Search"
                  onChange={(e) => {
                    handleSelectChannel(e.target.itemData);
                  }}
                  placeholder={'Please select a channel'}
                  value={selectedChannel}
                  width={200}
                />
              </div>
            </div>

            {/* Number of Weeks Input Field */}
            {/* <div className="architecture-banner-channel-selector">
              <div>{renderNumericTextbox()}</div>
            </div> */}
          </div>
        }
      />
      <div className="architecture-content">
        {/* {isLoading ? <div style={{ height: 'calc(100vh - 100px)' }}><BootstrapSpinner /></div> : <Scheduler {...schedulerProps} />} */}
        {/* {isLoading ? <div style={{ height: 'calc(100vh - 100px)' }}><BootstrapSpinner /></div>
          :  */}
        {activeCAComponent}
        {/* } */}
      </div>
      <ConfirmLeaveModal
        show={showPrompt}
        onSave={() => {
          handleSaveDirty(); // handleSaveDirty
        }}
        onDiscard={confirmNavigation}
        onHide={cancelNavigation}
      />

      <ConfirmLeaveModal
        show={showDirtyForm}
        onSave={() => {
          handleSaveDirty();
        }}
        onDiscard={handleDiscardDirtyForm}
        onHide={() => setShowDirtyForm(false)}
      />
    </div>
  );
};

export default ChannelArchitecture;
